import { useEffect, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useApiClient } from './use-api-client.js';
import { useServers } from './use-servers.js';
import { loadPrefs } from '../services/notification-prefs.js';

// The app icon's badge (#938): the decisions waiting on the servers this device follows, from the inboxes the app
// already reads (same queries as the server pages). A push sets it while the app is closed; the open app keeps it right
// when a decision is handled elsewhere. Nothing where the Badging API is missing (Android, Firefox).

type Badging = { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };

export function useAppBadge(): void {
  const api = useApiClient();
  const { data: servers } = useServers();
  // Read once per mount, not at every render
  const prefs = useMemo(() => loadPrefs(), []);
  const ids = (servers ?? []).map((s) => s.id).filter((id) => prefs.scope === 'all' || prefs.servers.includes(id));
  const inboxes = useQueries({
    queries: ids.map((id) => ({ queryKey: ['inbox', id], queryFn: () => api.inbox.list(id), refetchInterval: 60_000 })),
  });
  const ready = servers !== undefined && inboxes.every((q) => q.isSuccess);
  const count = inboxes.reduce((n, q) => n + (q.data?.length ?? 0), 0);
  useEffect(() => {
    const nav = navigator as Navigator & Badging;
    if (!ready || !nav.setAppBadge || !nav.clearAppBadge) return;
    void (count > 0 ? nav.setAppBadge(count) : nav.clearAppBadge()).catch(() => undefined);
  }, [ready, count]);
}
